/* eslint-disable no-undef */
import Vue from 'vue';
import Vuex from 'vuex';
import VueCookies from 'vue-cookies';
import cloneDeep from 'lodash.clonedeep';
import Endpoints from './admin/constants/api/endpoint/endpoint.constants';
import AjaxDataService from './services/ajax.service';
import Columns from './admin/constants/column-name/column-name.constants';

Vue.use(Vuex);
Vue.use(VueCookies);

export default new Vuex.Store({
  state: {
    AutoCompletes: {
      Unit: {
        sortBy: Columns.Unit.unitName.field,
        endPoint: Endpoints.Unit.autoComplete,
        searchEndPoint: Endpoints.Unit.data,
        canUseCancelToken: false,
        dataSource: []
      },
      Severity: {
        sortBy: 'SeverityName',
        endPoint: Endpoints.Severity.all,
        dataSource: []
      },
      Fluid: {
        sortBy: Columns.Fluid.fluidName.field,
        endPoint: Endpoints.Fluid.autoComplete,
        dataSource: []
      },
      FluidManufacturer: {
        sortBy: Columns.FluidManufacturer.fluidManufacturerName.field,
        endPoint: Endpoints.FluidManufacturer.autoComplete,
        dataSource: []
      },
      FluidBrand: {
        sortBy: Columns.FluidBrand.fluidBrandName.field,
        endPoint: Endpoints.FluidBrand.autoComplete,
        dataSource: []
      },
      FluidClass: {
        sortBy: Columns.FluidClass.fluidClass.field,
        endPoint: Endpoints.FluidClass.autoComplete,
        dataSource: []
      },
      FluidWeight: {
        sortBy: Columns.FluidWeight.fluidWeightName.field,
        endPoint: Endpoints.FluidWeight.autoComplete,
        dataSource: []
      },
      FluidBaselineSample: {
        sortBy: Columns.FluidBaselineSample.fluidBaselineSampleDate.field,
        endPoint: Endpoints.FluidBaselineSample.autoComplete,
        dataSource: []
      },
      FluidAction: {
        sortBy: Columns.FluidAction.fluidActionName.field,
        endPoint: Endpoints.FluidAction.all,
        dataSource: []
      },
      Comment: {
        sortBy: Columns.Comment.commentCode.field,
        sortType: 'number',
        endPoint: Endpoints.Comment.all,
        dataSource: []
      },
      Customer: {
        sortBy: null,
        endPoint: Endpoints.Customer.autoComplete,
        dataSource: []
      },
      ManufacturerModel: {
        sortBy: null,
        endPoint: Endpoints.ManufacturerModel.autoComplete,
        dataSource: []
      },
      UnitType: {
        sortBy: null,
        endPoint: Endpoints.UnitType.autoComplete,
        dataSource: []
      },
      ThirdParty: {
        sortBy: null,
        endPoint: Endpoints.ThirdParty.autoComplete,
        dataSource: []
      },
      UnitOfMeasure: {
        sortBy: Columns.UnitOfMeasure.unitOfMeasureName.field,
        endPoint: Endpoints.UnitOfMeasure.all,
        dataSource: []
      },
      Clarity: {
        sortBy: Columns.Clarity.clarityResultName.field,
        endPoint: Endpoints.Clarity.data,
        dataSource: []
      },
      Elc: {
        sortBy: 'elcPassFail',
        dataSource: [{ elcPassFail: '' }, { elcPassFail: 'Pass' }, { elcPassFail: 'Fail' }]
      },
      OilResult: {
        sortBy: Columns.OilResult.oilResultName.field,
        endPoint: Endpoints.OilResult.data,
        dataSource: []
      },
      Precipitate: {
        sortBy: Columns.Precipitate.precipitateResultName.field,
        endPoint: Endpoints.Precipitate.data,
        dataSource: []
      }
    }
  },
  getters: {
    /*
      Find the nested autocomplete item.
      Ex:
        monikers = ["foo", "bar"];
        return state.AutoCompletes.foo.bar;
    */
    getAutoCompleteData: state => (...monikers) => {
      let { AutoCompletes: data } = state;
      monikers.forEach(moniker => {
        data = data[moniker];
      });

      return typeof data === 'object' ? cloneDeep(data) : data;
    }
  },
  mutations: {
    setDataSource(state, dataSource) {
      dataSource.data.sort((a, b) => {
        // Remove sort if sortBy is for Fluids, sorting done by backend.
        if (dataSource.sortBy === Columns.Fluid.fluidName.field) {
          return 0;
        }
        // Sort by integers
        if (dataSource.sortType !== undefined && dataSource.sortType === 'number') {
          return a[dataSource.sortBy] - b[dataSource.sortBy];
        }

        if (dataSource.sortBy === Columns.FluidBaselineSample.fluidBaselineSampleDate.field) {
          return new Date(b[dataSource.sortBy]) - new Date(a[dataSource.sortBy]);
        }

        // Sort by strings
        if (a[dataSource.sortBy] < b[dataSource.sortBy]) {
          return -1;
        }

        if (a[dataSource.sortBy] > b[dataSource.sortBy]) {
          return 1;
        }

        return 0;
      });
      // eslint-disable-next-line no-param-reassign
      state.AutoCompletes[dataSource.storeName].dataSource = dataSource.data;
    }
  },
  actions: {
    async loadDataSource({ commit, state }, payload) {
      const { storeName, searchKey, searchValue } = payload;
      // eslint-disable-next-line no-param-reassign
      state.AutoCompletes[storeName].dataSource = [];
      let getParams = {
        pageSize: 0,
        orderBy: this.orderBy,
        [searchKey]: searchValue
      };
      const storeData = state.AutoCompletes[storeName];
      const endPoint = storeData?.searchEndPoint || storeData?.endPoint;
      const sortBy = storeData?.sortBy;
      const sortType = storeData?.sortType;

      if (storeName === 'FluidBaselineSample') {
        getParams = {
          ...getParams,
          fluidTypeNameQuery: payload.FluidTypeNameQuery,
          fluidIDQuery: payload.FluidIDQuery,
          searchText: searchValue
        };
      }

      if (storeName === 'FluidWeight') {
        getParams = {
          ...getParams,
          fluidClassQuery: payload.FluidClassQuery
        };
      }

      if (storeName === 'Fluid' || storeName === 'FluidBrand') {
        getParams = {
          ...getParams,
          fluidTypeIDQuery: payload.FluidTypeIDQuery
        };
      }

      if (endPoint !== undefined) {
        const { data } = await AjaxDataService.getData(endPoint, getParams);
        commit('setDataSource', { storeName, data, sortBy, sortType });
      }
    }
  }
});
